import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserIcon, SpeakerXMarkIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface BlockedUser {
  id: string;
  username: string;
  avatar?: string;
}

const ContentFiltersPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [mutedWords, setMutedWords] = useState<string[]>([]);
  const [newWord, setNewWord] = useState('');

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/content-filters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBlockedUsers(data.blockedUsers || []);
        setMutedWords(data.mutedWords || []);
      }
    } catch (error) {
      console.error('Failed to load content filters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (id: string) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/content-filters/blocked-users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchFilters();
      }
    } catch (error) {
      console.error('Failed to unblock user:', error);
    }
  };

  const handleAddMutedWord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWord.trim()) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/content-filters/muted-words`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ word: newWord }),
      });
      if (res.ok) {
        setNewWord('');
        await fetchFilters();
      }
    } catch (error) {
      console.error('Failed to add muted word:', error);
    }
  };

  const handleRemoveMutedWord = async (word: string) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/content-filters/muted-words/${encodeURIComponent(word)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchFilters();
      }
    } catch (error) {
      console.error('Failed to remove muted word:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/dashboard/settings')}
        className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
      >
        ← Back to Settings
      </button>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Content Filters</h1>
      <p className="mb-6 text-sm text-gray-600">
        Block users and mute words to customize your experience
      </p>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Blocked Users</h2>
        {blockedUsers.length === 0 ? (
          <div className="text-gray-500 text-sm">No blocked users</div>
        ) : (
          <ul className="space-y-3">
            {blockedUsers.map((user) => (
              <li key={user.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.username} className="w-8 h-8 rounded-full" />
                  ) : (
                    <UserIcon className="w-8 h-8 text-gray-400" />
                  )}
                  <span className="text-sm font-medium text-gray-900">{user.username}</span>
                </div>
                <button
                  onClick={() => handleUnblock(user.id)}
                  className="px-3 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Unblock
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Muted Words</h2>
        <form onSubmit={handleAddMutedWord} className="flex items-center space-x-3 mb-4">
          <input
            type="text"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            placeholder="Add word to mute"
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
          >
            Add
          </button>
        </form>
        {mutedWords.length === 0 ? (
          <div className="text-gray-500 text-sm">No muted words</div>
        ) : (
          <ul className="space-y-2">
            {mutedWords.map((word) => (
              <li key={word} className="flex items-center justify-between">
                <span className="text-sm text-gray-900">{word}</span>
                <button
                  onClick={() => handleRemoveMutedWord(word)}
                  className="px-3 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ContentFiltersPage;
